import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Smartphone, Monitor, Camera, X as XIcon, ShoppingCart, Users, Zap, Shield, BarChart3, Bell, CreditCard, Headphones, Palette, Globe } from 'lucide-react';

const DEFAULT_PHONE_IMG = "https://static.prod-images.emergentagent.com/jobs/23b5f19d-1a39-4e03-91cd-806fad7065f2/images/87f3ec1f7249e32441b9863e63815bd574d173eb0e164c1fbb814952be2d153c.jpeg";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Pricing = () => {
  const [phoneImg, setPhoneImg] = useState(DEFAULT_PHONE_IMG);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const kioskClicksRef = useRef([]);
  const [kioskRevealed, setKioskRevealed] = useState(false);
  const kioskSetupClicksRef = useRef([]);
  const [kioskSetupRevealed, setKioskSetupRevealed] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/admin/phone-image`).then(r => {
      if (r.ok) setPhoneImg(`${API_URL}/api/admin/phone-image?t=${Date.now()}`);
    }).catch(() => {});
  }, []);

  const handleUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const code = prompt('Enter passcode:');
    if (!code) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('passcode', code);
      const res = await fetch(`${API_URL}/api/admin/phone-image`, { method: 'POST', body: form });
      if (res.ok) setPhoneImg(`${API_URL}/api/admin/phone-image?t=${Date.now()}`);
      else alert('Wrong passcode');
    } catch { alert('Upload error'); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleResetImage = useCallback(async () => {
    const code = prompt('Enter passcode to reset:');
    if (!code) return;
    const res = await fetch(`${API_URL}/api/admin/phone-image?passcode=${code}`, { method: 'DELETE' });
    if (res.ok) setPhoneImg(DEFAULT_PHONE_IMG);
    else alert('Wrong passcode');
  }, []);

  const comparison = [
    { feature: 'Commission per order', hungrmates: '0%', delivery: '15-30%' },
    { feature: 'Monthly cost', hungrmates: 'Flat $99/mo', delivery: 'Variable + commission' },
    { feature: 'Order limits', hungrmates: 'Unlimited', delivery: 'No limit but costly' },
    { feature: 'Customer data ownership', hungrmates: 'You own it', delivery: 'They keep it' },
    { feature: 'Your own branded app', hungrmates: 'iOS + Android', delivery: 'Their brand' },
    { feature: 'Push notifications', hungrmates: 'Included', delivery: 'Not available' },
  ];

  return (
    <div style={{ position: 'relative' }}>

      {/* Hero */}
      <section style={{ background: '#1a1a2e', padding: '5rem 1.5rem 4rem', textAlign: 'center', borderBottom: '4px solid #FFD700' }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <div style={{ display: 'inline-block', padding: '6px 16px', background: '#22c55e', color: '#fff', borderRadius: '4px', fontWeight: 800, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', border: '3px solid #000', boxShadow: '3px 3px 0px 0px #000', marginBottom: '1.5rem' }}>
            ONE PLAN. ONE PRICE.
          </div>
          <h1 style={{ fontFamily: "'Bangers', cursive", fontSize: 'clamp(2.5rem, 5vw, 4rem)', color: '#FFD700', letterSpacing: '0.03em', lineHeight: 1.1, marginBottom: '1rem' }}>
            Your Restaurant's Own Mobile App
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500, fontSize: '1.15rem', lineHeight: 1.6, maxWidth: '620px', margin: '0 auto 2rem' }}>
            We take your current ordering page and build a branded iOS & Android app around it. No order limits. No learning a new system. One price.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap' }}>
            {['No Contracts', 'Cancel Anytime'].map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Check size={18} color="#22c55e" strokeWidth={3} />
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)' }}>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Plan */}
      <section data-testid="pricing-plan-section" style={{ padding: '4rem 1.5rem 5rem', background: '#FAFAFA', borderBottom: '4px solid #000' }}>
        <div className="container" style={{ maxWidth: '1100px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: '3rem', alignItems: 'start' }}>

            {/* Left — Phone */}
            <div data-testid="phone-column">
              <div style={{ position: 'relative', display: 'inline-block', width: '100%', textAlign: 'center' }}>
                <img src={phoneImg} alt="Your Restaurant App" style={{ width: '100%', maxWidth: '340px', maxHeight: '500px', objectFit: 'contain', height: 'auto', display: 'block', margin: '0 auto', mixBlendMode: phoneImg === DEFAULT_PHONE_IMG ? 'multiply' : 'normal', borderRadius: phoneImg !== DEFAULT_PHONE_IMG ? '32px' : '0' }} />
                <div style={{ position: 'absolute', bottom: '8px', right: 'calc(50% - 150px)', display: 'flex', gap: '0.4rem' }}>
                  <button
                    data-testid="upload-phone-btn"
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      width: '36px', height: '36px', borderRadius: '50%', border: '2px solid #000',
                      background: '#FFD700', color: '#000', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '2px 2px 0px 0px #000', opacity: uploading ? 0.5 : 0.7,
                      transition: 'opacity 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.opacity = 1}
                    onMouseLeave={e => e.currentTarget.style.opacity = 0.7}
                    title="Upload customer app screenshot"
                  >
                    <Camera size={16} />
                  </button>
                  {phoneImg !== DEFAULT_PHONE_IMG && (
                    <button
                      data-testid="reset-phone-btn"
                      onClick={handleResetImage}
                      style={{
                        width: '36px', height: '36px', borderRadius: '50%', border: '2px solid #000',
                        background: '#FF3B30', color: '#fff', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '2px 2px 0px 0px #000', opacity: 0.7,
                        transition: 'opacity 0.2s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.opacity = 1}
                      onMouseLeave={e => e.currentTarget.style.opacity = 0.7}
                      title="Reset to default"
                    >
                      <XIcon size={14} />
                    </button>
                  )}
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUpload} style={{ display: 'none' }} />
              </div>

              {/* Price Card */}
              <div data-testid="price-card" style={{ textAlign: 'center', marginTop: '2rem', padding: '2rem 1.5rem', background: '#fff', border: '4px solid #000', borderRadius: '12px', boxShadow: '6px 6px 0px 0px #22c55e' }}>
                <div style={{ fontFamily: "'Bangers', cursive", fontSize: '4.5rem', color: '#000', lineHeight: 1 }}>
                  $99<span style={{ fontSize: '1.25rem', color: '#888' }}>/month</span>
                </div>
                <div style={{ fontSize: '0.9rem', color: '#555', fontWeight: 600, marginBottom: '1.5rem', marginTop: '0.25rem' }}>
                  Everything included. No hidden fees.
                </div>
                <Link to="/book-demo" data-testid="get-started-btn" className="btn-primary" style={{ width: '100%', fontSize: '1.1rem', padding: '16px 24px', background: '#22c55e', borderColor: '#000' }}>
                  Get Started Today <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
                </Link>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '1.25rem', textAlign: 'left' }}>
                  {['Free Setup & App Build', 'Launch in 7-10 Days', 'We Build It Around Your Current System'].map((b, i) => (
                    <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <Check size={16} color="#22c55e" strokeWidth={3} />
                      <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#333' }}>{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right — What's Included */}
            <div data-testid="plan-details">
              <h2 style={{ fontFamily: "'Bangers', cursive", fontSize: '2rem', color: '#000', letterSpacing: '0.03em', marginBottom: '0.5rem' }}>
                Everything You Need. Nothing You Don't.
              </h2>
              <p style={{ color: '#555', fontWeight: 500, fontSize: '1rem', lineHeight: 1.6, marginBottom: '2rem' }}>
                We take your existing ordering page and wrap it in a beautiful, branded mobile app — listed on the App Store and Google Play. Your customers keep ordering the same way. You just get more of them.
              </p>

              {/* Feature Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2.5rem' }}>
                {[
                  { icon: <Smartphone size={22} />, iconBg: '#22c55e', title: 'iOS & Android App', desc: 'Your brand on every phone. Listed on both app stores.' },
                  { icon: <ShoppingCart size={22} />, iconBg: '#6A0DAD', title: 'Unlimited Orders', desc: 'No caps. No per-order fees. Process as many as you want.' },
                  { icon: <Zap size={22} />, iconBg: '#FF8C00', title: 'Built Around Your System', desc: 'We wrap your current ordering page. No retraining needed.' },
                  { icon: <Shield size={22} />, iconBg: '#3B82F6', title: 'Zero Commissions', desc: 'Keep 100% of your revenue. No middleman taking a cut.' },
                  { icon: <Bell size={22} />, iconBg: '#EF4444', title: 'Push Notifications', desc: 'Send unlimited notifications to bring customers back.' },
                  { icon: <Users size={22} />, iconBg: '#EC4899', title: 'Customer Database', desc: 'Own your customer data. Build direct relationships.' },
                  { icon: <BarChart3 size={22} />, iconBg: '#10B981', title: 'Analytics Dashboard', desc: 'Track orders, revenue, and customer behavior.' },
                  { icon: <CreditCard size={22} />, iconBg: '#F59E0B', title: 'Payment Processing', desc: 'Secure payments built right in. Cards, Apple Pay, Google Pay.' },
                  { icon: <Palette size={22} />, iconBg: '#8B5CF6', title: 'Custom Branding', desc: 'Your logo, colors, and menu — 100% your restaurant.' },
                  { icon: <Globe size={22} />, iconBg: '#0EA5E9', title: 'Ordering Website', desc: 'A branded online ordering page that matches your app.' },
                  { icon: <Headphones size={22} />, iconBg: '#64748B', title: '24/7 Support', desc: 'We handle the tech so you can focus on the food.' },
                  { icon: <ArrowRight size={22} />, iconBg: '#22c55e', title: 'Free Setup & Launch', desc: 'We build it, train your team, and get you live fast.' },
                ].map((f, i) => (
                  <div key={i} data-testid={`feature-${i}`} style={{
                    display: 'flex', gap: '0.75rem', padding: '1rem',
                    background: '#fff', border: '2px solid #e5e7eb', borderRadius: '10px',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = f.iconBg; e.currentTarget.style.boxShadow = `3px 3px 0px 0px ${f.iconBg}`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div style={{
                      width: '44px', height: '44px', borderRadius: '10px', background: f.iconBg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0,
                    }}>{f.icon}</div>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#000', marginBottom: '0.15rem' }}>{f.title}</div>
                      <div style={{ color: '#888', fontSize: '0.78rem', fontWeight: 500, lineHeight: 1.4 }}>{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bottom CTA */}
              <div style={{
                padding: '1.5rem 2rem', background: '#FFD700', border: '4px solid #000',
                borderRadius: '12px', boxShadow: '6px 6px 0px 0px #000',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem',
              }}>
                <div>
                  <div style={{ fontFamily: "'Bangers', cursive", fontSize: '1.5rem', color: '#000', letterSpacing: '0.03em' }}>
                    Ready to see your restaurant in an app?
                  </div>
                  <div style={{ color: '#333', fontWeight: 600, fontSize: '0.9rem' }}>
                    Book a free demo — we'll show you exactly how it works.
                  </div>
                </div>
                <Link to="/book-demo" data-testid="demo-cta-btn" className="btn-primary" style={{ padding: '14px 28px', fontSize: '1rem', background: '#000', color: '#FFD700', borderColor: '#000', whiteSpace: 'nowrap' }}>
                  Book a Free Demo <ArrowRight size={18} style={{ marginLeft: '0.5rem' }} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section style={{ padding: '1rem 1.5rem', background: '#fff', borderBottom: '4px solid #000' }}>
        <div className="container" style={{ maxWidth: '900px', display: 'flex', justifyContent: 'center', gap: '3rem', flexWrap: 'wrap' }}>
          {[
            '4.9/5 from 500+ restaurant owners',
            'Trusted by restaurants across the USA',
            'We build it. You own it.',
          ].map((t, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              {i === 0 && <span style={{ color: '#FFD700', fontWeight: 800 }}>&#9733;&#9733;&#9733;&#9733;&#9733;</span>}
              {i > 0 && <Check size={16} color="#22c55e" strokeWidth={3} />}
              <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#555' }}>{t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* KioskMates Hardware */}
      <section style={{ padding: '4rem 1.5rem', background: '#fff', borderBottom: '4px solid #000' }}>
        <div className="container" style={{ maxWidth: '1100px' }}>
          <h2 className="heading-2" style={{ textAlign: 'center', marginBottom: '2rem', color: '#000' }}>Hardware Add-Ons</h2>
          <div data-testid="hbot-section" className="comic-card" style={{ padding: '2rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '2rem', flexWrap: 'wrap', justifyContent: 'center', borderColor: '#FFD700', boxShadow: '8px 8px 0px 0px #FFD700' }}>
            <img src="/hbot.png" alt="H-Bot" style={{ width: '180px', height: 'auto' }} />
            <div style={{ flex: '1 1 280px', minWidth: '240px' }}>
              <div style={{ display: 'inline-block', padding: '4px 12px', background: '#FFD700', border: '3px solid #000', borderRadius: '4px', fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', boxShadow: '2px 2px 0px 0px #000', marginBottom: '0.75rem' }}>Meet the Sidekick</div>
              <h3 style={{ fontFamily: "'Bangers', cursive", fontSize: '2rem', color: '#000', letterSpacing: '0.03em', marginBottom: '0.5rem' }}>H-Bot</h3>
              <p style={{ color: '#333', fontWeight: 600, fontSize: '1.05rem', marginBottom: '0.75rem', lineHeight: 1.5 }}>
                Captain H's trusty sidekick. H-Bot powers your KioskMates self-ordering kiosk — taking orders, upselling combos, and keeping the line moving while you focus on the food.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {['Self-Ordering', 'Smart Upsells', 'Auto-Reset', 'Always On'].map((tag, i) => (
                  <span key={i} style={{ padding: '4px 12px', background: '#FFF9DB', border: '2px solid #000', borderRadius: '4px', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase' }}>{tag}</span>
                ))}
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.25rem' }}>
            <div data-testid="addon-kioskmates-byod" className="comic-card" style={{ padding: '1.5rem', cursor: 'pointer', WebkitUserSelect: 'none', userSelect: 'none' }}
              onClick={() => { const now=Date.now(); kioskClicksRef.current=[...kioskClicksRef.current,now].filter(t=>now-t<3000); if(kioskClicksRef.current.length>=5&&!kioskRevealed) setKioskRevealed(true); }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '8px', background: '#FFD700', border: '3px solid #000', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem', boxShadow: '3px 3px 0px 0px #000' }}><Smartphone size={24} /></div>
              <h4 style={{ fontWeight: 800, textTransform: 'uppercase', marginBottom: '0.25rem' }}>KioskMates</h4>
              <p style={{ color: '#555', fontSize: '0.875rem', marginBottom: '1rem', fontWeight: 500, lineHeight: 1.5 }}>Self-ordering kiosk software. Use your own tablet or touchscreen device.</p>
              {kioskRevealed ? (
                <div style={{ animation: 'promoReveal 0.5s ease-out' }}><div style={{ fontFamily: "'Bangers', cursive", fontSize: '2rem', color: '#000' }}>$99<span style={{ fontSize: '1rem', color: '#666' }}>/month</span></div></div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                  {['Increase average order size', 'Reduce wait times & labor costs', 'Upsell combos automatically'].map((v, i) => (
                    <div key={i} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}><Check size={14} color="#6A0DAD" strokeWidth={3} /><span style={{ color: '#333', fontWeight: 600, fontSize: '0.8rem' }}>{v}</span></div>
                  ))}
                </div>
              )}
              <span style={{ display: 'inline-block', marginTop: '0.75rem', padding: '4px 12px', borderRadius: '4px', background: '#D1FAE5', border: '2px solid #000', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase' }}>BYOD — Bring Your Own Device</span>
              <div style={{ marginTop: '1rem' }}><a href="https://kioskmates.com/" target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ padding: '8px 16px', fontSize: '0.85rem' }} onClick={(e) => e.stopPropagation()}>Learn More</a></div>
            </div>
            <div data-testid="addon-kiosk-setup" className="comic-card" style={{ padding: '1.5rem', borderColor: '#6A0DAD', boxShadow: '8px 8px 0px 0px #6A0DAD', cursor: 'pointer', WebkitUserSelect: 'none', userSelect: 'none' }}
              onClick={() => { const now=Date.now(); kioskSetupClicksRef.current=[...kioskSetupClicksRef.current,now].filter(t=>now-t<3000); if(kioskSetupClicksRef.current.length>=5&&!kioskSetupRevealed) setKioskSetupRevealed(true); }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '8px', background: '#6A0DAD', border: '3px solid #000', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem', boxShadow: '3px 3px 0px 0px #000' }}><Monitor size={24} color="#fff" /></div>
              <h4 style={{ fontWeight: 800, textTransform: 'uppercase', marginBottom: '0.25rem' }}>KioskMates Full Setup</h4>
              <p style={{ color: '#555', fontSize: '0.875rem', marginBottom: '1rem', fontWeight: 500, lineHeight: 1.5 }}>Self-ordering kiosk tablet included with payment terminal. We handle everything.</p>
              {kioskSetupRevealed ? (
                <div style={{ animation: 'promoReveal 0.5s ease-out' }}><div style={{ fontFamily: "'Bangers', cursive", fontSize: '2rem', color: '#000' }}>$499<span style={{ fontSize: '1rem', color: '#666' }}> setup fee</span></div></div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                  {['Everything included — just plug in', 'Hardware + software + installation', 'Start taking orders on day one'].map((v, i) => (
                    <div key={i} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}><Check size={14} color="#6A0DAD" strokeWidth={3} /><span style={{ color: '#333', fontWeight: 600, fontSize: '0.8rem' }}>{v}</span></div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.75rem' }}>
                {['Kiosk tablet included', 'Payment terminal included', 'Full setup & installation'].map((item, j) => (
                  <div key={j} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}><Check size={16} color="#6A0DAD" strokeWidth={3} /><span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{item}</span></div>
                ))}
              </div>
              <div style={{ marginTop: '1rem' }}><a href="https://kioskmates.com/" target="_blank" rel="noopener noreferrer" className="btn-purple" style={{ padding: '8px 16px', fontSize: '0.85rem' }} onClick={(e) => e.stopPropagation()}>Learn More</a></div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section style={{ padding: '5rem 1.5rem', background: '#FFF9DB', borderBottom: '4px solid #000' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2 className="heading-2" style={{ textAlign: 'center', marginBottom: '3rem', color: '#000' }}>HungrMates vs Delivery Apps</h2>
          <div style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', border: '4px solid #000', boxShadow: '8px 8px 0px 0px #000' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '1rem', padding: '1.25rem', background: '#FFD700', fontWeight: 800, borderBottom: '4px solid #000', textTransform: 'uppercase', fontSize: '0.9rem' }}>
              <div>Feature</div>
              <div style={{ textAlign: 'center', color: '#6A0DAD' }}>HungrMates</div>
              <div style={{ textAlign: 'center', color: '#666' }}>Delivery Apps</div>
            </div>
            {comparison.map((row, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '1rem', padding: '1.25rem', borderBottom: i < comparison.length - 1 ? '2px solid #000' : 'none', alignItems: 'center' }}>
                <div style={{ fontWeight: 700 }}>{row.feature}</div>
                <div style={{ textAlign: 'center', fontWeight: 800, color: '#6A0DAD', background: '#F3E8FF', padding: '0.5rem', borderRadius: '4px', border: '2px solid #6A0DAD' }}>{row.hungrmates}</div>
                <div style={{ textAlign: 'center', color: '#666', fontWeight: 500 }}>{row.delivery}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Link */}
      <section style={{ padding: '3rem 1.5rem', background: '#1a1a2e', borderBottom: '4px solid #FFD700', textAlign: 'center' }}>
        <div className="container" style={{ maxWidth: '700px' }}>
          <h2 style={{ fontFamily: "'Bangers', cursive", fontSize: '2rem', color: '#FFD700', marginBottom: '1rem' }}>Want to see your ROI?</h2>
          <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '1.5rem', fontWeight: 500 }}>Calculate your savings vs delivery apps and see how much more you could be making.</p>
          <Link to="/roi" className="btn-primary" style={{ fontSize: '1rem', padding: '14px 32px', background: '#FFD700', color: '#000', borderColor: '#000' }}>
            Open ROI Calculator <ArrowRight size={18} style={{ marginLeft: '0.5rem' }} />
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: '5rem 1.5rem', background: '#FFF9DB', borderBottom: '4px solid #000' }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <h2 className="heading-2" style={{ textAlign: 'center', marginBottom: '3rem', color: '#000' }}>Common Questions</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[
              { q: 'How does it work?', a: 'We take your current ordering page and build a custom branded mobile app around it. Your customers order the same way — just now through your own app on iOS and Android.' },
              { q: 'Is there a setup fee?', a: 'No. Setup, app build, training, and launch support are all included in the $99/mo.' },
              { q: 'Do I need to learn a new system?', a: 'Nope. We build around what you already use. No retraining, no switching platforms, no headaches.' },
              { q: 'Are there any order limits?', a: 'No limits. Process as many orders as you want — $99/mo stays the same whether you do 10 orders or 10,000.' },
              { q: 'Can I cancel anytime?', a: 'Yes — no contracts. We have to earn your business every month.' },
              { q: 'How long does it take to launch?', a: 'Most restaurants are live within 7-10 days from sign-up.' },
              { q: 'What about payment processing fees?', a: 'Standard credit card processing fees apply (typically 2.9% + $0.30 per transaction), but there are zero HungrMates commission fees on any order.' },
            ].map((faq, i) => (
              <div key={i} className="comic-card" style={{ padding: '1.25rem' }}>
                <h3 style={{ fontWeight: 800, marginBottom: '0.5rem', textTransform: 'uppercase', fontSize: '0.95rem' }}>{faq.q}</h3>
                <p style={{ color: '#444', lineHeight: 1.5, fontWeight: 500 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="halftone-bg" style={{ padding: '5rem 1.5rem', background: '#6A0DAD', textAlign: 'center', position: 'relative' }}>
        <div className="container" style={{ maxWidth: '600px', position: 'relative', zIndex: 1 }}>
          <h2 className="heading-2" style={{ marginBottom: '1.5rem', color: '#FFD700' }}>Ready to Own Your Orders?</h2>
          <p style={{ color: 'rgba(255,255,255,0.85)', marginBottom: '2rem', fontWeight: 500, fontSize: '1.125rem' }}>Book a free demo and see your restaurant in its own mobile app.</p>
          <Link to="/book-demo" className="btn-primary" style={{ fontSize: '1.125rem', padding: '16px 32px' }}>
            Book a Free Demo <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
          </Link>
        </div>
      </section>

      <style>{`
        @keyframes promoReveal {
          0% { opacity: 0; transform: scale(0.8); }
          50% { transform: scale(1.05); }
          100% { opacity: 1; transform: scale(1); }
        }
        @media (max-width: 900px) {
          [data-testid="pricing-plan-section"] > div > div { grid-template-columns: 1fr !important; }
          [data-testid="plan-details"] > div[style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; }
          [data-testid="phone-column"] { position: static !important; }
        }
      `}</style>
    </div>
  );
};
